import React from 'react';
import {
  Navigate,
  useLocation,
} from 'react-router-dom';
import { Loader2 } from 'lucide-react';

import { useAuth } from '@/contexts/AuthContext';
import { useCommandCenterAuthority } from '@/hooks/useCommandCenterAuthority';
import {
  buildLoginDestination,
  resolveRouteAccess,
  type RouteAccessLevel,
} from '@/governor/routing/RouteAccessAuthority';

interface RouteAccessBoundaryProps {
  readonly level:
    Exclude<RouteAccessLevel, 'public'>;
  readonly children: React.ReactNode;
}

const RouteAccessBoundary: React.FC<
  RouteAccessBoundaryProps
> = ({
  level,
  children,
}) => {
  const location = useLocation();

  const {
    isAuthenticated,
    isStaff,
    loading: authLoading,
  } = useAuth();

  const commandCenter =
    useCommandCenterAuthority(
      level === 'command-center',
    );

  if (
    authLoading ||
    (
      level === 'command-center' &&
      commandCenter.loading
    )
  ) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-fuchsia-600" />
      </div>
    );
  }

  if (
    level === 'command-center' &&
    commandCenter.status === 'ERROR'
  ) {
    return (
      <div
        role="alert"
        className="min-h-screen flex items-center justify-center bg-slate-50 px-4"
      >
        <div className="w-full max-w-lg rounded-2xl border border-amber-200 bg-white p-6 shadow-sm">
          <h1 className="text-xl font-bold text-slate-950">
            Command Center access could not be verified
          </h1>

          <p className="mt-3 text-sm text-slate-700">
            Access remains blocked until the live principal authority check succeeds.
          </p>

          <p className="mt-3 rounded-xl bg-amber-50 p-3 text-sm text-amber-900">
            Command Center authority verification failed.
          </p>

          <button
            type="button"
            onClick={commandCenter.retry}
            className="mt-4 rounded-xl bg-violet-700 px-4 py-2 text-sm font-bold text-white"
          >
            Retry verification
          </button>
        </div>
      </div>
    );
  }

  const decision =
    resolveRouteAccess(
      level,
      isAuthenticated,
      isStaff,
      commandCenter.allowed,
    );

  if (!decision.allowed) {
    if (
      decision.destination ===
      '/login'
    ) {
      return (
        <Navigate
          replace
          to={buildLoginDestination(
            location.pathname,
            location.search,
            location.hash,
          )}
        />
      );
    }

    return (
      <Navigate
        replace
        to="/app"
      />
    );
  }

  return <>{children}</>;
};

export default RouteAccessBoundary;
